"use client";

import React, { useEffect, useRef } from "react";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageTrademania2Schema } from "@/sanity/pages/PageTrademania2Template";
import { Artboard, Chip, RevealWords, prefersReducedMotion, revealLines } from "@/components/trademania-2/shared";
import { SYSTEM_TILES, SYSTEM_TILES_MOBILE } from "@/components/trademania-2/SystemTiles";

import notch from "@/public/trademania-2/sys-notch.svg";

// centre of the Figma frame the tile positions are measured from
const CX = 960;
const CY = 565;
const ORBIT_RX = 560;
const ORBIT_RY = 390;
const ORBIT_TILES = [...SYSTEM_TILES]
  .sort((a, b) => Math.atan2(a.y + a.h / 2 - CY, a.x + a.w / 2 - CX) - Math.atan2(b.y + b.h / 2 - CY, b.x + b.w / 2 - CX))
  .map((tile, i) => ({ ...tile, angle: -Math.PI * 0.75 + (i * Math.PI * 2) / SYSTEM_TILES.length }));

// phone ring (Figma 430 × 941 frame, px): the ellipse every tile sits on, the tile size and the
// pitch along a row; each row wraps after its four tiles
const P_CX = 215;
const P_CY = 464;
const P_RX = 647;
const P_RY = 316;
const P_TILE = 206;
const P_LOOP = 4 * 232;
// how far the rows travel: px per second on their own, and over the scroll into view
const P_DRIFT = 18;
const P_SCROLL = 360;

const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const inOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const rem = (px: number) => `${px / 8}rem`;
// a phone tile's centre `dx` from the ring centre, wrapped into its row's loop
const wrapRow = (dx: number) => ((((dx + P_LOOP / 2) % P_LOOP) + P_LOOP) % P_LOOP) - P_LOOP / 2;
// ...and its height on the ellipse: the top row bows up, the bottom row bows down
const ringY = (dx: number, row: 0 | 1) => (row ? 1 : -1) * P_RY * Math.sqrt(Math.max(0, 1 - (dx / P_RX) ** 2));
// tiles fade out just before they wrap round; on phones that happens off-screen, on the wider
// screens up to lg it keeps them from popping
const ringOpacity = (dx: number) => clamp((P_LOOP / 2 - Math.abs(dx)) / 64);

function Copy({ data }: { data: PageTrademania2Schema }) {
  return (
    <>
      <Chip text={data?.systemChip} theme="dark" slashes />
      <div className="flex w-full flex-col items-center gap-[2.5rem] lg:gap-[2.75rem]">
        <h2
          ref={revealLines}
          data-reveal
          className="whitespace-nowrap font-owners text-[7rem] font-medium leading-none tracking-[.02em] text-white lg:font-geist lg:text-[12.5rem] lg:leading-[.9] lg:tracking-[-.0216em]">
          <RevealWords text={data?.systemTitle} />
        </h2>
        {data?.systemText && (
          <p className="font-geist text-[2.5rem] font-medium leading-[1.5] text-white/60 lg:max-w-[67.375rem] lg:text-white/[.56]">
            {data.systemText}
          </p>
        )}
      </div>
      {data?.systemCTA && <CTACornerButton data={data.systemCTA} />}
    </>
  );
}

export default function System({ data }: { data: PageTrademania2Schema }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tileRefs = useRef<(HTMLDivElement | null)[]>([]);
  const phoneFrameRef = useRef<HTMLDivElement>(null);
  const phoneTileRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const frame = frameRef.current;
    const phoneFrame = phoneFrameRef.current;
    if (!frame || !phoneFrame || prefersReducedMotion()) return;

    const t0 = performance.now();
    let raf = 0;

    // Figma px -> screen px follows the root font size (the tiles are laid out in rem), so the ring
    // and the tiles share one scale at any width (above 1920px the rem no longer tracks the width)
    let k = 1;
    const measure = () => (k = parseFloat(getComputedStyle(document.documentElement).fontSize) / 8);
    measure();
    window.addEventListener("resize", measure);
    // the drift only runs while the section is on screen, so it is met close to the Figma state
    let drift = 0;
    let rotation = 0;
    let last = t0;

    const phone = (dt: number) => {
      const rect = phoneFrame.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom < 0 || rect.top > vh) return;
      drift += dt * P_DRIFT;
      // 0 when the section enters the viewport, 1 once its centre reaches the viewport centre
      const progress = clamp((vh - rect.top) / (vh / 2 + rect.height / 2));
      const shift = drift + (progress - 1) * P_SCROLL;
      SYSTEM_TILES_MOBILE.forEach((tile, i) => {
        const el = phoneTileRefs.current[i];
        if (!el) return;
        const dx = wrapRow(tile.x + P_TILE / 2 - P_CX + (tile.row ? -shift : shift));
        el.style.transform = `translate3d(${(dx * k).toFixed(2)}px,${(ringY(dx, tile.row) * k).toFixed(2)}px,0)`;
        el.style.opacity = ringOpacity(dx).toFixed(3);
      });
    };

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (window.innerWidth < 993) return phone(dt);
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom < 0 || rect.top > vh) return;
      // 0 when the section enters the viewport, 1 once its centre reaches the viewport centre
      const progress = clamp((vh - rect.top) / (vh / 2 + rect.height / 2));
      // the fly-out is done by the time the section is ~40% in, so its top never reads empty
      const e = inOut(clamp(progress / 0.55));
      rotation += dt * 0.06;

      if (contentRef.current) contentRef.current.style.opacity = String(clamp((progress - 0.2) / 0.3));

      ORBIT_TILES.forEach((tile, i) => {
        const el = tileRefs.current[i];
        if (!el) return;
        const angle = tile.angle + rotation + (progress - 1) * 0.9;
        const radiusScale = (0.35 + 0.65 * e) * k;
        const x = Math.cos(angle) * ORBIT_RX * radiusScale;
        const y = Math.sin(angle) * ORBIT_RY * radiusScale;
        el.style.transform = `translate3d(${x}px,${y}px,0) scale(${0.8 + 0.2 * e})`;
        el.style.zIndex = e > 0.5 ? "1" : String(10 + i);
      });
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <section className="relative z-[1] bg-[#020202] text-white">
      {/* phones: Figma frame 430 × 941; every tile is centred on the ring centre and moved onto the
          ring by its transform (the Figma spot until the loop takes over) */}
      <div ref={phoneFrameRef} className="relative h-[117.625rem] overflow-hidden lg:hidden">
        {SYSTEM_TILES_MOBILE.map(({ key, x, row, Tile }, i) => {
          const dx = wrapRow(x + P_TILE / 2 - P_CX);
          return (
            <div
              key={key}
              ref={(el) => {
                phoneTileRefs.current[i] = el;
              }}
              className="absolute will-change-transform"
              style={{
                left: `calc(50% - ${rem(P_TILE / 2)})`,
                top: rem(P_CY - P_TILE / 2),
                width: rem(P_TILE),
                transform: `translate3d(${rem(dx)},${rem(ringY(dx, row))},0)`,
                opacity: ringOpacity(dx),
              }}
            >
              <Artboard width={P_TILE} height={P_TILE} className="rounded-[1.875rem]">
                <Tile />
              </Artboard>
            </div>
          );
        })}
        <div className="absolute inset-x-[2rem] top-[37rem] flex flex-col items-center gap-[2.5rem] text-center">
          <Copy data={data} />
        </div>
      </div>

      {/* desktop: Figma frame 1920 × 1216; the tiles hang from the section's centre, like the copy */}
      <div ref={frameRef} className="relative hidden h-[152rem] overflow-hidden lg:block">
        {ORBIT_TILES.map(({ key, w, h, Tile, angle }, i) => (
          <div
            key={key}
            ref={(el) => {
              tileRefs.current[i] = el;
            }}
            className="absolute will-change-transform"
            style={{ left: `calc(50% - ${rem(w / 2)})`, top: `calc(50% - ${rem(h / 2)})`, width: rem(w), transform: `translate3d(${rem(Math.cos(angle) * ORBIT_RX)},${rem(Math.sin(angle) * ORBIT_RY)},0)` }}
          >
            <Artboard width={w} height={h} className="rounded-[1.875rem]">
              <Tile />
            </Artboard>
          </div>
        ))}

        <div
          ref={contentRef}
          className="absolute left-1/2 top-1/2 z-[3] flex w-[70.875rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[4rem] text-center"
        >
          <Copy data={data} />
        </div>
      </div>

      {/* tab hanging into the next section */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={notch.src}
        alt=""
        aria-hidden
        width={560}
        height={36}
        className="absolute left-1/2 top-[calc(100%-1px)] h-[2.625rem] w-[37.5rem] max-w-none -translate-x-1/2 rotate-180 lg:h-[4.5rem] lg:w-[70rem]"
      />
    </section>
  );
}
